/**
 * Resolve the post-login route destination from a user's capabilities.
 *
 * Pure function. Does not read from Vuex, Pinia, Vue Router, or any global
 * state. Callers must resolve the capabilities list (e.g. via
 * useAccountStore().capabilities or the Vuex equivalent) and pass it in.
 *
 * Mapping (checks in order; first match wins):
 *   1. superadmin.access  -> /admin
 *   2. distributor.access -> role-aware distributor landing:
 *        - WITH distributor.reporting.read  -> /distributor (dashboard)
 *        - WITHOUT it (team member, e.g. sales_executive) -> /distributor/stores
 *      The bare /distributor landing renders the dashboard-summary, which the
 *      backend gates on distributor.reporting.read. A team member who lacks
 *      that cap would 403 there; land them on the Stores list instead (a
 *      surface their store-lifecycle caps can reach). Mirrors the invite-accept
 *      redirect in AddCredentials.vue (owner -> /distributor/setup, team member
 *      -> /distributor/stores).
 *   3. portal.access      -> /portal
 *   4. shopper.shop       -> /store/:campaignSlug (only if campaignSlug is non-empty)
 *   5. otherwise          -> /login
 *
 * A shopper.shop holder with no campaignSlug falls through to /login because
 * a shopper without store context has nowhere meaningful to land.
 *
 * @param {Object} params
 * @param {string[] | null | undefined} params.capabilities
 *   Array of capability key strings. Null / undefined / non-array values are
 *   treated as "no capabilities" and yield /login.
 * @param {string} [params.campaignSlug]
 *   Campaign slug for shopper landing. Required for shopper-only callers.
 * @param {string} [params.returnTo]
 *   Where the user was headed before being bounced to login (GH #3088).
 *   Wins over the role landing when it is a safe same-origin path. See
 *   `isSafeReturnPath` for what "safe" excludes and why.
 * @returns {string} A route path string. Never null.
 */

/**
 * Is `value` a path we may send a freshly-authenticated user to?
 *
 * GH #3088 introduced this because the approval email deep-links into
 * the app, so a signed-out client has to survive a login round-trip and
 * come back. The return path arrives as a URL query param, which makes
 * it ATTACKER-CONTROLLABLE: anyone can mail a victim a crafted
 * `/login?next=...`. So this is an allow-list, not a block-list.
 *
 * Rejected, and why:
 *   - anything not starting with `/` -- not a same-document path.
 *   - `//host/...` -- protocol-relative; browsers resolve it to ANOTHER
 *     ORIGIN despite looking like a path. This is the classic
 *     open-redirect bypass.
 *   - any BACKSLASH anywhere. Browsers normalise `\` to `/` in URLs, so
 *     `/\evil.com` is delivered as `//evil.com` and leaves the origin
 *     while passing a naive `startsWith("//")` check. Found by probing
 *     this function directly rather than by reading it -- the first
 *     version of this guard let `/\evil.com` through.
 *   - anything containing `:` -- carries a scheme (`https:`,
 *     `javascript:`, `data:`).
 *   - `/login` itself -- a redirect loop, not a destination.
 *
 * Note `%2f%2fevil.com` is deliberately NOT rejected: the router
 * matches it as a single literal path segment, so it resolves to a
 * 404 inside our own app rather than to another origin.
 */
export function isSafeReturnPath(value) {
  if (typeof value !== "string" || !value) return false;
  if (value.length > MAX_RETURN_PATH_LENGTH) return false;
  // Whitespace and control characters: browsers STRIP tab / CR / LF from
  // URLs before parsing, so `/\t/evil.com` is delivered as `//evil.com`.
  if (CONTROL_OR_SPACE.test(value)) return false;
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;
  if (value.includes("\\")) return false;
  if (value.includes(":")) return false;
  const path = value.split("?")[0].split("#")[0];
  if (path === "/login") return false;
  return true;
}

// A return path longer than this is not a page anyone linked to; it is a
// payload. Also keeps the /login URL well under proxy line limits.
const MAX_RETURN_PATH_LENGTH = 2048;
// C0 controls, space, DEL.
// eslint-disable-next-line no-control-regex
const CONTROL_OR_SPACE = /[\u0000-\u0020\u007f]/;

// Neutral entry paths: the user did not ask for a page, so there is nothing
// to return to and the role landing is the right destination.
export const NEUTRAL_ENTRY_PATHS = new Set(["/", "/admin"]);

// Query keys / hash keys that carry a credential. A return path is written
// into the address bar, browser history and proxy logs, so a secret must
// never ride in it. Dropped, not rejected: the page itself is still a fine
// destination. (Storefront `#r=<resume code>` never reaches here -- the
// storefront is public -- but the strip is defence in depth.)
const SENSITIVE_KEYS = new Set([
  "token",
  "access_token",
  "id_token",
  "refresh_token",
  "auth_token",
  "code",
  "r",
  "resume",
  "password",
  "secret",
]);

function keyOf(pair) {
  const raw = pair.split("=")[0];
  try {
    return decodeURIComponent(raw).toLowerCase();
  } catch (_e) {
    return raw.toLowerCase();
  }
}

function dropSensitive(pairs) {
  return pairs.filter((pair) => pair && !SENSITIVE_KEYS.has(keyOf(pair)));
}

function stripSecrets(fullPath) {
  const hashAt = fullPath.indexOf("#");
  const beforeHash = hashAt === -1 ? fullPath : fullPath.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : fullPath.slice(hashAt + 1);
  const queryAt = beforeHash.indexOf("?");
  const path = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const query = queryAt === -1 ? "" : beforeHash.slice(queryAt + 1);

  const keptQuery = dropSensitive(query.split("&")).join("&");
  // A hash that is `key=value` pairs (a resume code, an OAuth fragment) is
  // checked per key; a plain anchor (`#details`) is kept as is.
  const hashPairs = hash.split("&");
  const keptHash = hash.includes("=")
    ? dropSensitive(hashPairs).join("&")
    : hash;

  return path + (keptQuery ? `?${keptQuery}` : "") + (keptHash ? `#${keptHash}` : "");
}

/**
 * Build the `/login` URL for a bounce, carrying the page the user tried to
 * load as the `next` query param (GH #3088; one param name, read by
 * TheLoginPage and consumed by `postLoginRedirect`).
 *
 * The ONE builder for every path that sends someone to login (the
 * `requireToken` guard, the 401 interceptor, the store's 401 handler).
 * Anything `isSafeReturnPath` refuses, the neutral entry paths and the login
 * page itself yield the plain `/login` -- so an explicit Log out (which pushes
 * plain `/login`), a loop, or a hostile value never carries a stale target.
 * The value is URL-encoded so `?`, `&` and `#` in the target round-trip.
 *
 * @param {string} fullPath path + query + hash of the page being left
 * @returns {string} `/login`, plus `?next=<encoded>` when a safe return exists
 */
export function loginRedirectFor(fullPath) {
  if (!isSafeReturnPath(fullPath)) return "/login";
  const cleaned = stripSecrets(fullPath);
  const path = cleaned.split("?")[0].split("#")[0];
  if (NEUTRAL_ENTRY_PATHS.has(path)) return "/login";
  return `/login?next=${encodeURIComponent(cleaned)}`;
}

export function postLoginRedirect({
  capabilities,
  campaignSlug,
  returnTo,
} = {}) {
  const caps = Array.isArray(capabilities) ? capabilities : [];

  // An explicit destination beats the role landing -- but only for a
  // user who actually has a surface to land on. A caller with no
  // capabilities falls through to /login below regardless, since a deep
  // link cannot rescue a session that has no access to anything.
  if (caps.length > 0 && isSafeReturnPath(returnTo)) {
    return returnTo;
  }

  if (caps.includes("superadmin.access")) {
    return "/admin";
  }
  if (caps.includes("distributor.access")) {
    // Dashboard-capable users land on the dashboard; team members without the
    // dashboard cap land on the Stores list to avoid a summary-403 on /distributor.
    return caps.includes("distributor.reporting.read")
      ? "/distributor"
      : "/distributor/stores";
  }
  if (caps.includes("portal.access")) {
    return "/portal";
  }
  if (caps.includes("shopper.shop") && campaignSlug) {
    // P8 (storefront convergence, card q7EQ3a0b): the canonical shopper
    // Home is `/shop/<slug>` (the P5/P6 tree). The legacy `/store/<slug>`
    // now only REDIRECTS there, so land the shopper directly on /shop to
    // avoid a redundant redirect hop on every login.
    return `/shop/${campaignSlug}`;
  }
  return "/login";
}

export default postLoginRedirect;
